import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "../../shared/api/client";
import { planWorkbookResponseSchema, type PlanWorkbook, type WorkbookCell } from "../../../shared/production/plan-workbook";
import { workbookCellInput, workbookInputValue } from "./plan-workbook-input";

function responseFile(base64: string, name: string): File {
  const bytes = Uint8Array.from(atob(base64), char => char.charCodeAt(0));
  return new File([bytes], name, { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}
export function usePlanWorkbookWorkspace(file: File | null, planId: string | null, onEdited: (file: File) => void) {
  const [workbook, setWorkbook] = useState<PlanWorkbook | null>(null);
  const [sheetName, setSheetName] = useState("");
  const [selectedAddress, setSelectedAddress] = useState("");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dirtyCount, setDirtyCount] = useState(0);
  const [zoom, setZoom] = useState(80);
  const draftFile = useRef<File | null>(null);
  const ownFile = useRef<File | null>(null);
  const run = useRef(0);
  const pending = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async (selectedFile: File | null, selectedPlan: string | null, signal: AbortSignal) => {
    const id = ++run.current;
    setWorkbook(null); setError(""); setDirtyCount(0); setSelectedAddress(""); setInput("");
    draftFile.current = null;
    if (!selectedFile && !selectedPlan) { setBusy(false); return; }
    setBusy(true);
    try {
      const body = new FormData();
      if (selectedFile) body.set("file", selectedFile);
      const raw: unknown = await apiRequest(selectedFile
        ? "/api/integrations/production/mikro/production-plan/workbook"
        : `/api/integrations/production/mikro/production-plans/${selectedPlan}/workbook`,
      selectedFile ? { method: "POST", body, signal } : { cache: "no-store", signal });
      const result = planWorkbookResponseSchema.parse(raw);
      if (id !== run.current || signal.aborted) return;
      draftFile.current = responseFile(result.fileBase64, result.workbook.fileName);
      setWorkbook(result.workbook); setSheetName(result.workbook.preferredSheet);
    } catch (cause) {
      if (id === run.current && !signal.aborted) setError(cause instanceof Error ? cause.message : "Excel açılamadı.");
    } finally { if (id === run.current && !signal.aborted) setBusy(false); }
  }, []);
  useEffect(() => {
    if (file && file === ownFile.current) return;
    const controller = new AbortController();
    void load(file, planId, controller.signal);
    return () => { controller.abort(); ++run.current; };
  }, [file, planId, load]);

  const sheet = workbook?.sheets.find(s => s.name === sheetName) ?? null;
  const selected = sheet?.rows.flatMap(row => row.cells).find(cell => cell.address === selectedAddress) ?? null;
  const select = (cell: WorkbookCell) => {
    if (selected?.address === cell.address) return true;
    if (selected?.editable && input !== workbookCellInput(selected)) { setError("Önce hücre düzeltmesini uygulayın veya vazgeçin."); return false; }
    setSelectedAddress(cell.address); setInput(workbookCellInput(cell)); setError("");
    return true;
  };
  const apply = async () => {
    if (!selected?.editable || !draftFile.current || pending.current) return;
    const id = run.current;
    pending.current = true; setBusy(true); setError("");
    try {
      const value = workbookInputValue(selected, input);
      if (value === selected.value) return;
      const body = new FormData(); body.set("file", draftFile.current);
      body.set("edits", JSON.stringify([{ sheet: sheetName, address: selected.address, value }]));
      const raw: unknown = await apiRequest("/api/integrations/production/mikro/production-plan/workbook", { method: "POST", body });
      const result = planWorkbookResponseSchema.parse(raw);
      if (id !== run.current) return;
      const updated = responseFile(result.fileBase64, result.workbook.fileName);
      draftFile.current = updated; ownFile.current = updated;
      const updatedCell = result.workbook.sheets.find(s => s.name === sheetName)?.rows.flatMap(r => r.cells).find(c => c.address === selected.address);
      setInput(workbookCellInput(updatedCell ?? null));
      setWorkbook(result.workbook); setDirtyCount(count => count + 1); onEdited(updated);
    } catch (cause) {
      if (id === run.current) setError(cause instanceof Error ? cause.message : "Düzeltme uygulanamadı. Yeniden deneyin.");
    } finally { pending.current = false; if (id === run.current) setBusy(false); }
  };
  const download = () => {
    if (!draftFile.current) return;
    const url = URL.createObjectURL(draftFile.current), anchor = document.createElement("a");
    anchor.href = url; anchor.download = dirtyCount ? draftFile.current.name.replace(/\.xlsx$/i, " (düzeltilmiş).xlsx") : draftFile.current.name;
    anchor.click(); window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const changeSheet = (name: string) => {
    if (selected?.editable && input !== workbookCellInput(selected)) { setError("Önce hücre düzeltmesini uygulayın veya vazgeçin."); return; }
    setSheetName(name); setSelectedAddress(""); setInput("");
  };
  const cancelInput = () => { setInput(workbookCellInput(selected)); setError(""); };
  const retry = () => { const controller = new AbortController(); void load(file, planId, controller.signal); };
  const hasCellDraft = Boolean(selected?.editable && input !== workbookCellInput(selected));
  const hasDrafts = dirtyCount > 0 || hasCellDraft;
  useEffect(() => {
    if (!hasDrafts) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasDrafts]);
  return { workbook, sheet, selected, input, setInput, inputRef, busy, error, dirtyCount, hasCellDraft, hasDrafts, zoom, setZoom, select, apply, cancelInput, download, changeSheet, retry };
}
